/**
 * 51. Suppliers — supplier master; tax ID; bank; status; performance;
 * contracts.
 *
 * Arabic and English (C7): every word from the catalogs; a supplier's status
 * is the glossary's word; open payables are an amount the locale writes in
 * the supplier's currency.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatMoney } from "@/lib/money";
import { CreateRecordButton } from "@/components/CreateRecordButton";
import { useCurrencyFormField } from "@/components/CurrencyField";

interface SupplierRow {
  id: string;
  supplierNo: string;
  name: string;
  taxId: string | null;
  currencyCode: string;
  paymentTerms: string | null;
  status: "Active" | "Blocked" | "Dormant";
  openPayables: string;
}

export function SuppliersPage() {
  const { t } = useTranslation();
  // Prefilled with the workspace currency, never a literal (CUR-2).
  const currencyField = useCurrencyFormField("currencyCode", true);
  const { data, isLoading } = useApiList<SupplierRow>(
    ["procurement", "suppliers"],
    "/procurement/suppliers",
  );

  return (
    <div>
      <PageHeader
        title={t("procurement.suppliers.title")}
        subtitle={t("procurement.suppliers.subtitle")}
        actions={
          <CreateRecordButton
            label={t("procurement.suppliers.new")}
            path="/suppliers"
            invalidate={["procurement", "suppliers"]}
            fields={[
              {
                name: "supplierNo",
                label: t("procurement.suppliers.supplierNumber"),
                required: true,
              },
              { name: "name", label: t("fields.name"), required: true },
              currencyField,
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("procurement.suppliers.list"), data.total)}>
          <DataTable<SupplierRow>
            rows={data.items}
            columns={[
              {
                key: "supplierNo",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.supplierNo}</span>
                ),
              },
              { key: "name", label: t("fields.name") },
              {
                key: "taxId",
                label: t("fields.taxId"),
                render: (r) =>
                  r.taxId ? <span className="atlas-mono">{r.taxId}</span> : "—",
              },
              { key: "currencyCode", label: t("fields.currency") },
              {
                key: "paymentTerms",
                label: t("procurement.suppliers.terms"),
                render: (r) => r.paymentTerms ?? "—",
              },
              {
                key: "openPayables",
                label: t("procurement.suppliers.openPayables"),
                numeric: true,
                render: (r) => formatMoney(r.openPayables, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} type="Supplier" />
                  ) : (
                    <Badge tone={r.status === "Blocked" ? "danger" : "default"}>
                      {statusLabel(t, "Supplier", r.status)}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
